import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom"; // ← NEW
import { Container, Spinner, Alert } from "reactstrap";

import DocumentViewer from "../components/DocumentViewer";
import { fileBadge, formatSize } from "helpers/helper_funcations";
import { get, post } from "helpers/api_helper";
import { useDispatch, useSelector } from "react-redux";
import { getDocuments, getMyDocuments } from "store/actions";

const DUMMY_VIDEO =
  "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4";

const TICK_MS = 1000;
const HEARTBEAT_MS = 15000;

const DocViewerPage = () => {
  const themeColor = useSelector((state) => state.theme.color);

  useEffect(() => {
    document.documentElement.style.setProperty("--scrollbar-color", themeColor);
  }, [themeColor]);

  const { user: loggedUser } = useSelector(state => state.Login);
  const isAdmin = [1, 2].includes(loggedUser?.role);
  const dispatch = useDispatch();
  const navigate = useNavigate(); // ← NEW
  const { id: docId } = useParams();

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [doc, setDoc] = useState(null);
  const [secureUrl, setSecureUrl] = useState(null);

  const [backLoading, setBackLoading] = useState(false); // ← NEW

  const tracker = useRef({
    startedAt: null,
    lastTick: null,
    accMsTotal: 0,
    accMsSinceLast: 0,
    visible: !document.hidden,
    videoPlaying: false,
  });
  const videoRef = useRef(null);
  const tickTimerRef = useRef(null);
  const heartbeatRef = useRef(null);
  const messageRef = useRef(null);

  const fetchSecureUrl = async (opts = { download: false }) => {
    if (!docId) return null;
    setLoading(true);
    try {
      const qs = opts.download ? "?download=true" : "";
      const json = await get(`/documents/${docId}/secure-url${qs}`);
      if (!json?.success) throw new Error(json?.message || "Failed to fetch document");

      setDoc(json.data?.document || null);
      if (!opts.download) setSecureUrl(json.data?.signedUrl || null);
      setMessage(null);
      if (!isAdmin) {
        dispatch(getMyDocuments());
      }
      return json.data?.signedUrl || null;
    } catch (e) {
      setMessage(e.message || "Failed to load document");
      if (!opts.download) {
        setDoc(null);
        setSecureUrl(null);
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const url = await fetchSecureUrl({ download: true });
      if (!url) return;

      const a = document.createElement("a");
      a.href = url;
      a.download = doc?.fileName || doc?.name || "file";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      // fallback: open in new tab
      window.open(secureUrl || fileUrl, "_blank", "noopener,noreferrer");
    }
  };

  useEffect(() => {
    setLoading(true);
    setMessage(null);
    setDoc(null);
    setSecureUrl(null);
    if (docId) fetchSecureUrl();
  }, [docId]);

  // helpers (place near top of file)
  const VIDEO_EXT_SET = new Set([
    "mp4", "m4v", "mov", "webm", "ogv", "avi", "mkv", "3gp", "hevc", "h265", "ts", "mts", "m2ts", "mpeg", "mpg",
  ]);

  const getExt = (s = "") => {
    try {
      // try URL first
      const u = new URL(s);
      s = u.pathname;
    } catch { }
    const m = String(s).toLowerCase().match(/\.([a-z0-9]+)(?:\?.*)?$/i);
    return m ? m[1] : "";
  };

  const looksLikeVideoByNameOrUrl = (doc) => {
    const candidates = [
      doc?.fileName,
      doc?.originalFilename,
      doc?.name,
      doc?.uploadMeta?.fileName,
      doc?.fileUrl,
      doc?.uploadMeta?.publicUrl,
    ].filter(Boolean);

    for (const s of candidates) {
      const ext = getExt(s);
      if (VIDEO_EXT_SET.has(ext)) return true;
    }
    return false;
  };

  const mimeType = useMemo(
    () => (doc?.mimeType || doc?.uploadMeta?.mimeType || "").toLowerCase(),
    [doc]
  );

  const isVideo = useMemo(() => {
    if (mimeType && /^video\//i.test(mimeType)) return true;
    if (mimeType === "application/octet-stream" || !mimeType) {
      return looksLikeVideoByNameOrUrl(doc);
    }
    if (mimeType === "video/hevc" || mimeType === "video/h265" || mimeType === "video/x-hevc") {
      return true;
    }
    return looksLikeVideoByNameOrUrl(doc);
  }, [mimeType, doc]);

  const fileUrl = useMemo(() => {
    if (secureUrl) return secureUrl;
    const url = doc?.fileUrl || doc?.uploadMeta?.publicUrl || null;
    if (!url && isVideo) return DUMMY_VIDEO;
    return url;
  }, [secureUrl, doc, isVideo]);

  const doTick = () => {
    const now = Date.now();
    const t = tracker.current;

    if (!t.startedAt) t.startedAt = new Date().toISOString();
    if (t.lastTick == null) t.lastTick = now;

    const shouldCount = t.visible && (!isVideo || t.videoPlaying);
    const delta = now - t.lastTick;

    if (shouldCount && delta > 0 && delta < 5000) {
      t.accMsTotal += delta;
      t.accMsSinceLast += delta;
    }
    t.lastTick = now;
  };
  useEffect(() => { messageRef.current = message; }, [message]);

  const sendHeartbeat = async (final = false) => {
    if (![3, 4].includes(Number(loggedUser?.role))) return;

    if (!docId || messageRef.current) return;
    const t = tracker.current;
    const secondsSinceLast = Math.floor(t.accMsSinceLast / 1000);
    const totalSeconds = Math.floor(t.accMsTotal / 1000);
    if (!final && secondsSinceLast <= 0) return;

    try {
      await post(`/documents/${docId}/telemetry`, {
        secondsSinceLast,
        totalSeconds,
        startedAt: t.startedAt,
        final,
        isVideo,
        userAgent: navigator.userAgent,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      t.accMsSinceLast = 0;
    } catch {
      // ignore
    }
  };

  const handleBackClick = async () => {
    if (backLoading) return;
    setBackLoading(true);
    try {
      await sendHeartbeat(true);
      if (isAdmin) {
        dispatch(getDocuments());
      } else {
        dispatch(getMyDocuments());
      }
    } finally {
      setBackLoading(false);
    }
  };

  useEffect(() => {
    tracker.current = {
      startedAt: null,
      lastTick: null,
      accMsTotal: 0,
      accMsSinceLast: 0,
      visible: !document.hidden,
      videoPlaying: false,
    };

    tickTimerRef.current = setInterval(doTick, TICK_MS);
    heartbeatRef.current = setInterval(() => sendHeartbeat(false), HEARTBEAT_MS);

    const onVis = () => {
      tracker.current.visible = !document.hidden;
      tracker.current.lastTick = Date.now();
    };
    document.addEventListener("visibilitychange", onVis);

    const onUnload = () => {
      doTick();
      sendHeartbeat(true);
    };
    window.addEventListener("beforeunload", onUnload);

    return () => {
      if (tickTimerRef.current) clearInterval(tickTimerRef.current);
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("beforeunload", onUnload);

      doTick();
      sendHeartbeat(true);
    };
  }, [docId, isVideo]);

  useEffect(() => {
    if (!isVideo) return;
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => { tracker.current.videoPlaying = true; tracker.current.lastTick = Date.now(); };
    const onPause = () => { doTick(); tracker.current.videoPlaying = false; tracker.current.lastTick = Date.now(); };
    const onEnded = onPause;

    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("ended", onEnded);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("ended", onEnded);
    };
  }, [isVideo, fileUrl]);

  // let proxied;
  // if (!isVideo) {
  //   const API = "http://localhost:5000";
  //   proxied = fileUrl ? `${API}/api/proxy/pdf?url=${encodeURIComponent(fileUrl)}` : null;
  // }

  if (!docId) {
    return (
      <Container className="py-5" style={{ marginTop: 60 }}>
        <Alert color="warning" className="mb-0">Document id is missing in the URL.</Alert>
      </Container>
    );
  }

  if (!!loading) {
    return (
      <div className="loading-container" style={{ width: "100%", height: "100dvh", display: "grid", placeItems: "center", marginTop: 75 }}>
        <div className="text-center">
          <Spinner style={{ width: "3rem", height: "3rem" }} />
          <div className="mt-2">Loading resource…</div>
        </div>
      </div>
    );
  }

  if (message) {
    return (
      <Container className="py-5" style={{ marginTop: 60 }}>
        <Alert color="danger" className="mb-0">{String(message)}</Alert>
      </Container>
    );
  }

  if (!doc) {
    return (
      <Container className="py-5" style={{ marginTop: 60 }}>
        <Alert color="warning" className="mb-0">Resource not found.</Alert>
      </Container>
    );
  }

  if (!fileUrl) {
    return (
      <Container className="py-5" style={{ marginTop: 60 }}>
        <Alert color="warning" className="mb-3">This resource doesn’t have an accessible URL yet.</Alert>
      </Container>
    );
  }

  return (
    <div className="page-content mb-0 pb-0">
      <Container fluid className="pt-8">
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 mt-1">
          <div className="d-flex align-items-center gap-2">

            {/* <Link
              to="/resources"
              onClick={handleBackClick}
              className={`btn btn-sm btn-outline-secondary me-2 ${backLoading ? "disabled" : ""}`}
              style={{ borderRadius: 13, fontSize: 13, minWidth: 75, padding: 7 }}
              title="Back"
            >
              <img
                src={require("../assets/images/back.png")}
                alt="Back"
                className="d-none d-sm-inline"
                height={25}
              />
              <img
                src={require("../assets/images/back.png")}
                alt="Back"
                className="d-inline d-sm-none"
                height={15}
              />
              {backLoading ? "Going back…" : "Back"}
            </Link> */}

            <div className="d-flex flex-column">
              <div className="text-muted file-text">
                {doc.fileName} • {fileBadge(doc?.mimeType || "") || "unknown"} • {formatSize(doc.size)}
              </div>
            </div>
          </div>

          <div className="mt-2 mt-lg-0">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              style={{
                borderWidth: 0,
                paddingTop: 10,
                paddingRight: 12,
                paddingBottom: 10,
                paddingLeft: 12,
                borderRadius: "10px",
                backgroundColor: "var(--scrollbar-color, #06427a)",
              }}
              onClick={handleDownload}
              disabled={backLoading}
            >
              Download
            </button>
          </div>
        </div>

        {isVideo ? (
          <div className="mt-3" style={{ background: "#000" }}>
            <video
              ref={videoRef}
              key={fileUrl}
              src={fileUrl}
              controls
              loop
              autoPlay
              muted
              playsInline
              preload="metadata"
              controlsList="nodownload noplaybackrate"
              disablePictureInPicture
              onContextMenu={(e) => e.preventDefault()}
              style={{ width: "100%", maxHeight: "78vh", display: "block" }}
            >
              Sorry, your browser doesn’t support embedded videos.
            </video>
          </div>
        ) : (
          <DocumentViewer fileUrl={fileUrl} mimeType={doc?.mimeType} fileName={doc?.fileName} />
        )}
      </Container>
    </div>
  );
};

export default DocViewerPage;
